import { useState, useCallback } from 'react';
import { useAnalytics } from '@johnlewispartnership/wtr-website-analytics';
import { useAuthentication } from '@johnlewispartnership/wtr-website-authentication/dist/context';
import { useCommonModal } from 'components/CommonModal';
import { urls } from 'constants/urls';
import { useShoppingContext } from 'contexts/ShoppingContext';
import { useSlotContext } from 'contexts/Slot';
import { useTrolley } from 'contexts/Trolley';
import orderOrchestrationServiceClient from 'services/orderOrchestration';
import { pathJoin } from 'utils/general';
import { CommonModalProps } from 'components/CommonModal/context';
import { GetOrderResponse, PlaceOrder409Error } from 'services/orderOrchestration/types';
import { SlotType } from 'services/slotOrchestration/types';
import { TrolleyItem } from 'services/graphql/types';
import { getInstantCheckoutTrolleyItems } from 'components/InstantCheckoutModalContents/utils';

const sendInstantCheckoutTransactionCompleteEvent = (
  trolleyItems?: TrolleyItem[],
  slotType?: SlotType,
  orderResponse?: GetOrderResponse,
) => {
  const instantCheckoutTrolleyItems = getInstantCheckoutTrolleyItems(
    trolleyItems,
    orderResponse?.orderLines,
  );
  const slotBranch =
    orderResponse?.slots &&
    (orderResponse.slots.length > 1
      ? orderResponse.slots.find(({ status }) => status === 'UNCONFIRMED')
      : orderResponse.slots[0]);
  window.navigator.sendBeacon(
    '/mfe/common/api/v1/instantCheckoutTransactionComplete',
    JSON.stringify({
      customerOrderId: orderResponse?.customerOrderId,
      branchId: slotBranch?.branchId,
      deliveryChargeAmount: orderResponse?.totals.estimated.deliveryCharge.amount,
      slotType,
      instantCheckoutTrolleyItems,
    }),
  );
};

const useInstantCheckout = (
  location: string,
  getInstantCheckoutErrorModal: (error: PlaceOrder409Error, location?: string) => CommonModalProps,
) => {
  const {
    shoppingContext: { customerOrderId },
  } = useShoppingContext();
  const {
    state: { token },
  } = useAuthentication();
  const { trackEvent } = useAnalytics();
  const { openModal } = useCommonModal();
  const { state: trolleyState } = useTrolley();
  const { slotContext } = useSlotContext();
  const [loading, setLoading] = useState(false);

  const handleInstantCheckout = useCallback(async () => {
    trackEvent({
      event: 'instant_checkout_click',
      instant_checkout: {
        location,
      },
    });
    setLoading(true);
    const response = await orderOrchestrationServiceClient.placeOrder({
      authHeader: token!.authHeader,
      customerOrderId,
      orderOptions: { instantCheckout: true },
    });

    if (!response.error) {
      sendInstantCheckoutTransactionCompleteEvent(
        trolleyState.trolley?.trolleyItems,
        slotContext.details?.slotType,
        response.order,
      );
      window.location.href = pathJoin(urls.orderConfirmation, customerOrderId);
    } else {
      openModal(getInstantCheckoutErrorModal(response.error, location));
    }

    return response;
  }, [
    customerOrderId,
    getInstantCheckoutErrorModal,
    location,
    openModal,
    slotContext,
    token,
    trackEvent,
    trolleyState,
  ]);
  return { handleInstantCheckout, loading };
};

export default useInstantCheckout;
